/**
 * Round 93r (Feb 2026) — Ex-Div Alert settings panel.
 *
 * Used in two contexts:
 *  1. On the Dividend Calendar page header — governs BOTH the portfolio
 *     and watchlist alert toggles + their per-window choices.
 *  2. On the Watchlist tab (Dashboard) — governs ONLY the watchlist
 *     alert toggle (pass `showWatchlistOnly`).
 *
 * Round 93r++ — Live preview list under each lead-time window block.
 * When the user selects any window button(s), a list renders below
 * showing matching upcoming ex-divs (Ticker · Ex-Div · Alert date).
 * Updates in real-time as windows are toggled. Collapses to 3 rows by
 * default with "Show more / less" footer when >3 alerts match.
 *
 * No per-ticker toggle by design — whole portfolio or nothing. Scope
 * for portfolio is "current portfolio" or "all portfolios".
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing, BellOff, Loader2, ChevronDown, ChevronUp, Megaphone, TrendingDown, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { value: 1, label: '1 day before' },
  { value: 3, label: '3 days before' },
  { value: 5, label: '5 days before' },
];

// Local date math — adds (-days) to a YYYY-MM-DD string and returns
// the resulting YYYY-MM-DD. Used to compute the alert-fire date for
// each (ex_div_date, window) pair.
function subtractDaysIso(iso, days) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

// Pretty-print a YYYY-MM-DD as "Jun 11, 2026" in local time (avoids
// the JS UTC-midnight off-by-one trap).
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function WindowChips({ disabled, selected, onToggle, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {WINDOWS.map((w) => {
        const active = selected.includes(w.value);
        return (
          <button
            key={w.value}
            type="button"
            disabled={disabled}
            onClick={() => onToggle(w.value)}
            className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
              disabled
                ? 'opacity-40 cursor-not-allowed border-border bg-muted text-muted-foreground'
                : active
                  ? 'border-violet-500 bg-violet-600 text-white hover:bg-violet-700'
                  : 'border-border bg-card text-foreground hover:bg-muted'
            }`}
            data-testid={`${testIdPrefix}-${w.value}d`}
          >
            {w.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Renders the filtered alert preview list for one source (portfolio or
 * watchlist). Filters `rows` to only those whose `days_until_ex`
 * intersects with the selected `windows`. Collapses to 3 rows with a
 * Show More toggle when there are more matches.
 */
function PreviewList({ rows, windows, sourceLabel, testIdPrefix, onStockClick }) {
  const [expanded, setExpanded] = useState(false);

  // For each row, compute every alert date it would generate under the
  // user's currently-selected windows. A single ticker may emit
  // multiple alert dates (e.g. ex-div in 5 days with windows={1,5} →
  // alerts fire today and on day 4). Each (ticker, ex_date, alert_date)
  // triple becomes one row in the preview.
  const expandedRows = useMemo(() => {
    const out = [];
    for (const r of rows || []) {
      for (const w of windows) {
        if (r.days_until_ex < w) continue;  // alert already fired in the past for this window
        out.push({
          ticker: r.ticker,
          name: r.name,
          ex_div_date: r.ex_div_date,
          alert_date: subtractDaysIso(r.ex_div_date, w),
          window: w,
          days_until_ex: r.days_until_ex,
        });
      }
    }
    // Sort: soonest alert_date first, then by ticker.
    out.sort((a, b) => {
      if (a.alert_date !== b.alert_date) return a.alert_date < b.alert_date ? -1 : 1;
      return a.ticker.localeCompare(b.ticker);
    });
    return out;
  }, [rows, windows]);

  if (!windows.length) return null;

  if (expandedRows.length === 0) {
    return (
      <div
        className="mt-2 rounded-md border border-dashed border-border bg-muted/20 p-2 text-[11px] text-muted-foreground font-semibold text-center"
        data-testid={`${testIdPrefix}-empty`}
      >
        No {sourceLabel.toLowerCase()} ex-divs in the next 5 days match these windows.
      </div>
    );
  }

  const visibleRows = expanded ? expandedRows : expandedRows.slice(0, 3);
  const hasMore = expandedRows.length > 3;

  return (
    <div className="mt-2 rounded-md border border-border bg-muted/10 overflow-hidden" data-testid={`${testIdPrefix}-list`}>
      {/* Round 93r+++ — switched from CSS grid (auto/auto cols) to a
          proper <table> so the Ex-Div and Alert column headers always
          sit centered above their data, regardless of how wide the
          ticker / date values render. */}
      <div className="max-h-[180px] overflow-y-auto">
        <table className="w-full text-[11px]" data-testid={`${testIdPrefix}-table`}>
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              <th className="px-2 py-1 text-left text-[9px] uppercase tracking-wide font-bold text-muted-foreground w-[34%]">
                Ticker
              </th>
              <th className="px-2 py-1 text-center text-[9px] uppercase tracking-wide font-bold text-muted-foreground w-[33%]">
                Ex-Div
              </th>
              <th className="px-2 py-1 text-center text-[9px] uppercase tracking-wide font-bold text-muted-foreground w-[33%]">
                Alert
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-testid={`${testIdPrefix}-rows`}>
            {visibleRows.map((r) => {
              const alertSoon = r.alert_date <= new Date().toISOString().slice(0, 10);
              return (
                <tr
                  key={`${r.ticker}-${r.ex_div_date}-${r.window}`}
                  data-testid={`${testIdPrefix}-row-${r.ticker}-${r.window}d`}
                >
                  <td className="px-2 py-1.5 text-left font-mono">
                    {onStockClick ? (
                      <button
                        type="button"
                        onClick={() => onStockClick(r.ticker)}
                        className="font-bold text-violet-700 dark:text-violet-300 hover:underline focus:outline-none focus:ring-2 focus:ring-violet-500/40 rounded-sm"
                        title={`Open ${r.ticker} stock details`}
                        data-testid={`${testIdPrefix}-ticker-${r.ticker}`}
                      >
                        {r.ticker}
                      </button>
                    ) : (
                      <span className="font-bold text-violet-700 dark:text-violet-300">{r.ticker}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono text-amber-700 dark:text-amber-300 font-semibold whitespace-nowrap">
                    {fmtDate(r.ex_div_date)}
                  </td>
                  <td
                    className={`px-2 py-1.5 text-center font-mono font-semibold whitespace-nowrap ${
                      alertSoon
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : 'text-foreground'
                    }`}
                    title={`Alert fires ${r.window} day${r.window === 1 ? '' : 's'} before ex-div`}
                  >
                    {fmtDate(r.alert_date)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Show more / less footer */}
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300 bg-muted/20 hover:bg-muted/40 transition-colors border-t border-border flex items-center justify-center gap-1"
          data-testid={`${testIdPrefix}-toggle`}
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Show less
              <ChevronUp className="w-3 h-3" />
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Show more ({expandedRows.length - 3} more)
              <ChevronDown className="w-3 h-3" />
            </>
          )}
        </button>
      )}
    </div>
  );
}

// Sep 2026 — bell alert the moment Massive publishes a declared amount
// (new cycle, raise, cut or amended figure). Off by default; one shared
// preference, so ticking it on the Portfolio, Watchlist or Calendar box is the same switch.
function DeclaredToggle({ checked, onChange, testId, who }) {
  return (
    <label className="flex items-start justify-between gap-2 cursor-pointer border-t border-border/60 pt-2.5">
      <span className="min-w-0">
        <span className="text-xs font-bold flex items-center gap-2">
          <Megaphone className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
          Dividend Declared alert
        </span>
        <span className="block text-[10px] text-muted-foreground leading-snug mt-0.5">
          Bell notification when the issuer declares the next payout for {who} — shows the amount and raise/cut vs the last payment.
        </span>
      </span>
      <input
        type="checkbox"
        className="h-4 w-4 accent-emerald-600 cursor-pointer shrink-0 mt-0.5"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
    </label>
  );
}

export default function ExDivAlertSettings({ showWatchlistOnly = false, onClose, onStockClick, children }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  // Round 93r++ — preview data feeding the live alert lists under each
  // window block. Fetched once on mount and after any prefs change
  // (so portfolio_scope updates re-scope the list).
  const [preview, setPreview] = useState({ portfolio: [], watchlist: [] });

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/users/me/exdiv-alert-prefs`);
      setPrefs(data?.prefs || null);
    } catch {
      toast.error('Could not load alert settings');
    }
  }, []);

  const fetchPreview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/exdiv-alerts/preview`);
      setPreview({
        portfolio: Array.isArray(data?.portfolio) ? data.portfolio : [],
        watchlist: Array.isArray(data?.watchlist) ? data.watchlist : [],
      });
    } catch {
      // silent — preview is non-essential, the settings still work
    }
  }, []);

  useEffect(() => { fetchPrefs(); fetchPreview(); }, [fetchPrefs, fetchPreview]);

  const save = useCallback(async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/users/me/exdiv-alert-prefs`, patch);
      setPrefs(data?.prefs || null);
      // Re-fetch preview when scope changes — the server-side scope
      // ('current' vs 'all') controls which portfolio_ids feed the
      // calendar, so the row list can shift.
      if ('portfolio_scope' in patch || 'portfolio_enabled' in patch || 'watchlist_enabled' in patch) {
        fetchPreview();
      }
    } catch (e) {
      toast.error('Could not save alert settings');
    } finally {
      setSaving(false);
    }
  }, [fetchPreview]);

  if (!prefs) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="exdiv-settings-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading alert settings…
      </div>
    );
  }

  const togglePortfolioWindow = (w) => {
    const next = prefs.portfolio_windows.includes(w)
      ? prefs.portfolio_windows.filter((x) => x !== w)
      : [...prefs.portfolio_windows, w];
    save({ portfolio_windows: next });
  };
  const toggleWatchlistWindow = (w) => {
    const next = prefs.watchlist_windows.includes(w)
      ? prefs.watchlist_windows.filter((x) => x !== w)
      : [...prefs.watchlist_windows, w];
    save({ watchlist_windows: next });
  };

  return (
    <div className="space-y-4" data-testid="exdiv-alert-settings">
      <div className="flex items-center gap-2">
        <BellRing className="w-4 h-4 text-violet-600 dark:text-violet-400" />
        <h3 className="text-sm font-bold tracking-tight">Ex-Dividend Alert Pop-up</h3>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
      </div>

      {/* Round 93r++ — line break between the two sentences + both
          bold (per user spec). */}
      <p className="text-[11px] text-foreground leading-relaxed">
        <span className="font-bold block">
          Shows a modal on your first dashboard visit each day with upcoming ex-dividend dates.
        </span>
        <span className="font-bold block mt-1">
          Pick which lead-time windows trigger it.
        </span>
      </p>

      {/* Portfolio block — hidden when showWatchlistOnly */}
      {!showWatchlistOnly && (
        <div className="rounded-lg border border-border bg-card p-3 space-y-3" data-testid="exdiv-settings-portfolio">
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <span className="text-xs font-bold flex items-center gap-2">
              {prefs.portfolio_enabled ? (
                <BellRing className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
              ) : (
                <BellOff className="w-3.5 h-3.5 text-muted-foreground" />
              )}
              Portfolio Stocks
            </span>
            <input
              type="checkbox"
              className="h-4 w-4 accent-violet-600 cursor-pointer"
              checked={!!prefs.portfolio_enabled}
              onChange={(e) => save({ portfolio_enabled: e.target.checked })}
              data-testid="exdiv-settings-portfolio-enabled"
            />
          </label>

          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1.5">Lead-time windows</p>
            <WindowChips
              disabled={!prefs.portfolio_enabled}
              selected={prefs.portfolio_windows}
              onToggle={togglePortfolioWindow}
              testIdPrefix="exdiv-settings-portfolio-window"
            />
            {prefs.portfolio_enabled && (
              <PreviewList
                rows={preview.portfolio}
                windows={prefs.portfolio_windows}
                sourceLabel="Portfolio"
                testIdPrefix="exdiv-settings-portfolio-preview"
                onStockClick={onStockClick}
              />
            )}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1.5">Scope</p>
            <div className="flex gap-1.5">
              {[
                { value: 'current', label: 'Current portfolio only' },
                { value: 'all', label: 'All my portfolios' },
              ].map((s) => {
                const active = prefs.portfolio_scope === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    disabled={!prefs.portfolio_enabled}
                    onClick={() => save({ portfolio_scope: s.value })}
                    className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
                      !prefs.portfolio_enabled
                        ? 'opacity-40 cursor-not-allowed border-border bg-muted text-muted-foreground'
                        : active
                          ? 'border-violet-500 bg-violet-600 text-white hover:bg-violet-700'
                          : 'border-border bg-card text-foreground hover:bg-muted'
                    }`}
                    data-testid={`exdiv-settings-portfolio-scope-${s.value}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <DeclaredToggle
            checked={!!prefs.declared_portfolio}
            onChange={(v) => save({ declared_portfolio: v })}
            testId="exdiv-settings-portfolio-declared"
            who="stocks you hold"
          />
          <label className="flex items-start justify-between gap-2 cursor-pointer border-t border-border/60 pt-2.5">
            <span className="min-w-0">
              <span className="text-xs font-bold flex items-center gap-2">
                <TrendingDown className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                Dividend Cuts / Raises alert
              </span>
              <span className="block text-[10px] text-muted-foreground leading-snug mt-0.5">
                Dashboard pop-up + bell row when a stock you hold cuts or raises its payout. Use "Don't show again" or the bell's Mute to silence one change — a later cut/raise on that stock shows again.
              </span>
            </span>
            <input
              type="checkbox"
              className="h-4 w-4 accent-rose-600 cursor-pointer shrink-0 mt-0.5"
              checked={!!prefs.div_change_portfolio}
              onChange={(e) => { save({ div_change_portfolio: e.target.checked }); window.dispatchEvent(new Event('div-change-alerts-changed')); }}
              data-testid="exdiv-settings-portfolio-div-change"
            />
          </label>
          <label className="flex items-start justify-between gap-2 cursor-pointer border-t border-border/60 pt-2.5">
            <span className="min-w-0">
              <span className="text-xs font-bold flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                Foreign withholding reclaim reminder
              </span>
              <span className="block text-[10px] text-muted-foreground leading-snug mt-0.5">
                Bell row after each payout from an ADR you hold whose country withholds more than the US treaty rate (Switzerland 35% vs 15%, Germany 26.4%, South Korea 22%, Denmark, Sweden, France…). Shows the dollar excess you can reclaim on taxable-account shares and what is lost for good on IRA / Roth shares.
              </span>
            </span>
            <input
              type="checkbox"
              className="h-4 w-4 accent-amber-600 cursor-pointer shrink-0 mt-0.5"
              checked={!!prefs.wht_reclaim_portfolio}
              onChange={(e) => save({ wht_reclaim_portfolio: e.target.checked })}
              data-testid="exdiv-settings-portfolio-wht-reclaim"
            />
          </label>
        </div>
      )}

      {/* Watchlist block */}
      <div className="rounded-lg border border-border bg-card p-3 space-y-3" data-testid="exdiv-settings-watchlist">
        <label className="flex items-center justify-between gap-2 cursor-pointer">
          <span className="text-xs font-bold flex items-center gap-2">
            {prefs.watchlist_enabled ? (
              <BellRing className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
            ) : (
              <BellOff className="w-3.5 h-3.5 text-muted-foreground" />
            )}
            Watchlist Stocks
          </span>
          <input
            type="checkbox"
            className="h-4 w-4 accent-violet-600 cursor-pointer"
            checked={!!prefs.watchlist_enabled}
            onChange={(e) => save({ watchlist_enabled: e.target.checked })}
            data-testid="exdiv-settings-watchlist-enabled"
          />
        </label>

        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1.5">Lead-time windows</p>
          <WindowChips
            disabled={!prefs.watchlist_enabled}
            selected={prefs.watchlist_windows}
            onToggle={toggleWatchlistWindow}
            testIdPrefix="exdiv-settings-watchlist-window"
          />
          {prefs.watchlist_enabled && (
            <PreviewList
              rows={preview.watchlist}
              windows={prefs.watchlist_windows}
              sourceLabel="Watchlist"
              testIdPrefix="exdiv-settings-watchlist-preview"
              onStockClick={onStockClick}
            />
          )}
        </div>

        <DeclaredToggle
          checked={!!prefs.declared_watchlist}
          onChange={(v) => save({ declared_watchlist: v })}
          testId="exdiv-settings-watchlist-declared"
          who="stocks you watch"
        />
      </div>

      {children}

      {onClose && (
        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-bold px-3 py-1.5 rounded-md bg-muted hover:bg-muted/70 transition-colors"
            data-testid="exdiv-settings-close-btn"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}
